import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { getSystemLogs, purgeSystemLogs } from "../api/adminApi";
import { Hero, Dropdown } from "../components/adminSurface/AdminSurface";
import { PulseArt } from "../components/adminSurface/heroArt";

const PAGE_SIZE = 100;
const EXPORT_PAGE_SIZE = 500;
const EXPORT_MAX_ROWS = 100000;

const LEVEL_META = {
  Information: { color: "#1d4ed8", bg: "#dbeafe" },
  Warning:     { color: "#b45309", bg: "#fef3c7" },
  Error:       { color: "#b91c1c", bg: "#fee2e2" },
  Critical:    { color: "#7c2d12", bg: "#fef2f2" },
};

const SOURCE_COLORS = {
  api:      { color: "#4f46e5", bg: "#ede9fe" },
  worker:   { color: "#0369a1", bg: "#e0f2fe" },
  gatherer: { color: "#047857", bg: "#d1fae5" },
  ui:       { color: "#7c3aed", bg: "#f5f3ff" },
  auth:     { color: "#b45309", bg: "#fef3c7" },
  agent:    { color: "#0f766e", bg: "#ccfbf1" },
  portal:   { color: "#be185d", bg: "#fce7f3" },
};

export default function SystemLogs() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [levels, setLevels]     = useState({ Information: true, Warning: true, Error: true, Critical: true });
  const [source, setSource]     = useState("");
  // Which machine wrote the line. Free text rather than a dropdown: SystemLogs is the largest
  // table in a long-lived install, so a DISTINCT over it to populate a picker is not a query
  // worth running on every page load.
  const [nodeId, setNodeId]     = useState("");
  const [timeRange, setTimeRange] = useState("24h");
  const [text, setText]         = useState("");

  const [items, setItems]   = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]   = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [purging, setPurging]   = useState(false);
  const [exporting, setExporting] = useState(false);

  // Debounce text search
  const debounceRef = useRef(null);
  const [debouncedText, setDebouncedText] = useState("");
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedText(text), 400);
    return () => clearTimeout(debounceRef.current);
  }, [text]);

  const nodeDebounceRef = useRef(null);
  const [debouncedNode, setDebouncedNode] = useState("");
  useEffect(() => {
    clearTimeout(nodeDebounceRef.current);
    nodeDebounceRef.current = setTimeout(() => setDebouncedNode(nodeId), 400);
    return () => clearTimeout(nodeDebounceRef.current);
  }, [nodeId]);

  function timeRangeToFrom(range) {
    const now = new Date();
    switch (range) {
      case "5m":    return new Date(now - 5 * 60 * 1000).toISOString();
      case "1h":    return new Date(now - 60 * 60 * 1000).toISOString();
      case "24h":   return new Date(now - 24 * 60 * 60 * 1000).toISOString();
      case "today": { const d = new Date(); d.setHours(0,0,0,0); return d.toISOString(); }
      case "7d":    return new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();
      default:      return undefined;
    }
  }

  const buildQueryArgs = useCallback(() => {
    const enabledLevelsList = Object.entries(levels).filter(([, v]) => v).map(([k]) => k);
    const allOn = enabledLevelsList.length === Object.keys(LEVEL_META).length;
    return {
      levels: allOn ? undefined : enabledLevelsList,
      source: source || undefined,
      nodeId: debouncedNode || undefined,
      from: timeRangeToFrom(timeRange),
      q: debouncedText || undefined,
      pageSize: PAGE_SIZE,
    };
  }, [levels, source, debouncedNode, timeRange, debouncedText]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getSystemLogs(token, buildQueryArgs());
      setItems(result.items ?? []);
      setNextCursor(result.nextCursor ?? null);
    } catch {
      setError(t("system.loadError", { defaultValue: "Failed to load logs" }));
    } finally {
      setLoading(false);
    }
  }, [token, buildQueryArgs, t]);

  const loadOlder = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const result = await getSystemLogs(token, { ...buildQueryArgs(), cursor: nextCursor });
      setItems((prev) => [...prev, ...(result.items ?? [])]);
      setNextCursor(result.nextCursor ?? null);
    } catch {
      setError(t("system.loadError", { defaultValue: "Failed to load logs" }));
    } finally {
      setLoadingMore(false);
    }
  }, [token, nextCursor, loadingMore, buildQueryArgs, t]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Scroll-to-highlight on navigate
  useEffect(() => {
    try {
      const id = location?.state?.highlightId;
      if (!id) return;
      setExpanded((s) => { const n = new Set(s); n.add(id); return n; });
      setTimeout(() => {
        const el = document.getElementById(`log-${id}`);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.style.transition = "background-color 300ms ease";
        el.style.backgroundColor = "#fef9c3";
        setTimeout(() => { el.style.backgroundColor = ""; }, 1800);
      }, 120);
      navigate(location.pathname, { replace: true, state: {} });
    } catch {}
  }, [items, location, navigate]);

  const filtered = items;

  async function handleExport() {
    if (exporting) return;
    setExporting(true);
    try {
      const baseArgs = { ...buildQueryArgs(), pageSize: EXPORT_PAGE_SIZE };
      const all = [];
      let cursor = null;
      let capped = false;
      while (true) {
        const args = cursor ? { ...baseArgs, cursor } : baseArgs;
        const result = await getSystemLogs(token, args);
        const batch = result.items ?? [];
        all.push(...batch);
        cursor = result.nextCursor ?? null;
        if (!cursor) break;
        if (all.length >= EXPORT_MAX_ROWS) { capped = true; break; }
      }
      exportCsv(all);
      if (capped) {
        alert(t("system.exportCapped", {
          max: EXPORT_MAX_ROWS.toLocaleString(),
          defaultValue: `Export capped at ${EXPORT_MAX_ROWS.toLocaleString()} rows. Narrow your filters and try again.`,
        }));
      }
    } catch (err) {
      console.error("System logs export failed", err);
      alert(t("system.exportError", { defaultValue: "Export failed." }));
    } finally {
      setExporting(false);
    }
  }

  async function handlePurge(days) {
    if (!window.confirm(t("system.purgeConfirm", { days, defaultValue: `Delete logs older than ${days} days?` }))) return;
    setPurging(true);
    try {
      const r = await purgeSystemLogs(token, days);
      alert(t("system.purgeSuccess", { count: r.deleted, defaultValue: `Deleted ${r.deleted} log entries.` }));
      fetchLogs();
    } catch {
      alert(t("system.purgeError", { defaultValue: "Purge failed." }));
    } finally { setPurging(false); }
  }

  return (
    <div className="app-root">
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />
      <div className="page-container">

        {/* ── Page header ── */}
        <Hero
          title={t("system.logs")}
          logo={<PulseArt />}
          actions={
            <>
              <button className="asf-hero__btn" onClick={fetchLogs} disabled={loading}>
                <RefreshIcon /> {t("system.refresh", { defaultValue: "Refresh" })}
              </button>
              <button className="asf-hero__btn asf-hero__btn--danger" onClick={() => handlePurge(30)} disabled={purging}>
                <TrashIcon /> {t("system.purge30", { defaultValue: "Purge >30 days" })}
              </button>
              <button className="asf-hero__btn" onClick={handleExport} disabled={exporting || loading}>
                <DownloadIcon /> {exporting ? t("system.exporting", { defaultValue: "Exporting…" }) : t("system.exportCsv")}
              </button>
            </>
          }
        />

        {/* ── Filter bar ── */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 16,
          flexWrap: "wrap",
          padding: "10px 14px",
          background: "#fff",
          border: "1.5px solid #e5e7eb",
          borderRadius: 10,
        }}>
          {/* Source pills */}
          <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginRight: 2 }}>
            {t("system.source", { defaultValue: "Source" })}
          </span>
          {[
            { value: "",         label: t("system.sourceAll", { defaultValue: "All" }) },
            { value: "api",      label: t("system.sourceApi", { defaultValue: "API" }) },
            { value: "worker",   label: t("system.sourceWorker", { defaultValue: "Worker" }) },
            { value: "gatherer", label: t("system.sourceGatherer", { defaultValue: "Gatherer" }) },
            { value: "auth",     label: t("system.sourceAuth", { defaultValue: "Auth" }) },
            { value: "agent",    label: t("system.sourceAgent", { defaultValue: "Agent" }) },
            { value: "portal",   label: t("system.sourcePortal", { defaultValue: "Portal" }) },
            { value: "ui",       label: t("system.sourceUi", { defaultValue: "UI" }) },
          ].map(({ value, label }) => {
            const active = source === value;
            const meta   = SOURCE_COLORS[value];
            return (
              <button
                key={value}
                type="button"
                onClick={() => setSource(value)}
                style={{
                  padding: "4px 12px",
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.12s",
                  border: active
                    ? `1.5px solid ${meta?.color ?? "#7c3aed"}`
                    : "1.5px solid #e5e7eb",
                  background: active ? (meta?.bg ?? "#f4f2fb") : "#fff",
                  color: active ? (meta?.color ?? "#6d28d9") : "#6b7280",
                }}
              >
                {label}
              </button>
            );
          })}

          <div style={{ width: 1, height: 22, background: "#e5e7eb", margin: "0 4px" }} />

          {/* Level checkboxes */}
          <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginRight: 2 }}>
            {t("system.level")}
          </span>
          {Object.entries(LEVEL_META).map(([lvl, meta]) => (
            <label key={lvl} style={{ display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={!!levels[lvl]}
                onChange={(e) => setLevels((s) => ({ ...s, [lvl]: e.target.checked }))}
                style={{ accentColor: meta.color }}
              />
              <span style={{
                fontSize: 11,
                fontWeight: 700,
                color: meta.color,
                background: meta.bg,
                padding: "2px 7px",
                borderRadius: 999,
              }}>
                {lvl.toUpperCase()}
              </span>
            </label>
          ))}

          <div style={{ width: 1, height: 22, background: "#e5e7eb", margin: "0 4px" }} />

          {/* Time range */}
          <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {t("system.timeLabel")}
          </span>
          <div style={{ minWidth: 132 }}>
            <Dropdown
              value={timeRange}
              onChange={setTimeRange}
              options={[
                { value: "all", label: t("timeRange.all") },
                { value: "5m", label: t("timeRange.last5m") },
                { value: "1h", label: t("timeRange.last1h") },
                { value: "24h", label: t("timeRange.last24h") },
                { value: "today", label: t("timeRange.today") },
                { value: "7d", label: t("timeRange.last7d") },
              ]}
            />
          </div>

          <div style={{ width: 1, height: 22, background: "#e5e7eb", margin: "0 4px" }} />

          {/* Node filter — which machine emitted the line */}
          <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {t("system.node", { defaultValue: "Node" })}
          </span>
          <div style={{ display: "flex", alignItems: "center", border: "1.5px solid #e5e7eb", borderRadius: 8, background: "#fff", overflow: "hidden", width: 150 }}>
            <input
              type="text"
              placeholder={t("system.nodeAll", { defaultValue: "All nodes" })}
              value={nodeId}
              onChange={(e) => setNodeId(e.target.value)}
              style={{ border: "none", outline: "none", padding: "6px 8px", fontSize: 12.5, width: "100%", fontFamily: "monospace" }}
            />
            {nodeId && (
              <button
                type="button"
                onClick={() => setNodeId("")}
                title={t("system.nodeClear", { defaultValue: "Show every node" })}
                style={{ border: "none", background: "transparent", cursor: "pointer", color: "#9ca3af", padding: "0 8px", fontSize: 15, lineHeight: 1 }}
              >
                ×
              </button>
            )}
          </div>

          <div style={{ width: 1, height: 22, background: "#e5e7eb", margin: "0 4px" }} />

          {/* Text search */}
          <div style={{ display: "flex", alignItems: "center", gap: 0, border: "1.5px solid #e5e7eb", borderRadius: 8, background: "#fff", overflow: "hidden", flex: "1 1 200px", minWidth: 180 }}>
            <span style={{ padding: "0 8px", color: "#9ca3af", display: "flex", alignItems: "center" }}>
              <SearchIcon />
            </span>
            <input
              type="text"
              placeholder={t("system.containsText")}
              value={text}
              onChange={(e) => setText(e.target.value)}
              style={{ border: "none", outline: "none", fontSize: 13, padding: "6px 8px 6px 0", background: "transparent", flex: 1, color: "#374151", minWidth: 0 }}
            />
            {text && (
              <button
                type="button"
                onClick={() => setText("")}
                style={{ border: "none", background: "transparent", padding: "0 8px", color: "#9ca3af", cursor: "pointer", fontSize: 16, lineHeight: 1 }}
              >×</button>
            )}
          </div>

          <button
            type="button"
            onClick={() => { setLevels({ Information: true, Warning: true, Error: true, Critical: true }); setSource(""); setNodeId(""); setTimeRange("24h"); setText(""); }}
            style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#6d28d9", whiteSpace: "nowrap" }}
          >
            {t("system.resetFilters")}
          </button>
        </div>

        {/* ── Table card ── */}
        <div className="logs-card" style={{ maxHeight: 560, overflow: "auto" }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "#9ca3af", fontSize: 14 }}>
              <SpinnerIcon />
              <div style={{ marginTop: 10 }}>{t("system.loading", { defaultValue: "Loading logs…" })}</div>
            </div>
          ) : error ? (
            <div style={{ padding: 40, textAlign: "center", color: "#b91c1c", fontSize: 14 }}>{error}</div>
          ) : filtered.length === 0 ? (
            <EmptyState />
          ) : (
            <table className="logs-table">
              <thead>
                <tr>
                  <th style={{ width: 148 }}>{t("system.timeHeader")}</th>
                  <th style={{ width: 110 }}>{t("system.levelHeader")}</th>
                  <th style={{ width: 90 }}>{t("system.source", { defaultValue: "Source" })}</th>
                  <th style={{ width: 130 }}>{t("system.node", { defaultValue: "Node" })}</th>
                  <th>{t("system.messageHeader")}</th>
                  <th style={{ width: 44 }} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => {
                  const isExp = expanded.has(e.id);
                  const lvlMeta = LEVEL_META[e.level] ?? { color: "#6b7280", bg: "#f3f4f6" };
                  const srcMeta = SOURCE_COLORS[e.source] ?? { color: "#6b7280", bg: "#f3f4f6" };
                  return (
                    <tr key={e.id} id={`log-${e.id}`}>
                      <td style={{ color: "#6b7280", fontSize: 12, whiteSpace: "nowrap" }}>
                        {formatDateTime(e.timestamp)}
                      </td>
                      <td>
                        <span style={{
                          display: "inline-block",
                          padding: "2px 8px",
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                          background: lvlMeta.bg,
                          color: lvlMeta.color,
                          letterSpacing: "0.03em",
                        }}>
                          {e.level.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          display: "inline-block",
                          padding: "2px 8px",
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 600,
                          background: srcMeta.bg,
                          color: srcMeta.color,
                        }}>
                          {e.source}
                        </span>
                      </td>
                      <td style={{ fontSize: 11.5, color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {e.nodeId ? (
                          <span
                            title={e.instanceId ? `${e.nodeId} · ${e.instanceId}` : e.nodeId}
                            style={{ fontFamily: "monospace" }}
                          >
                            {e.nodeId}
                            {e.instanceId && (
                              <span style={{ color: "#94a3b8" }}>{" · "}{shortInstance(e.instanceId)}</span>
                            )}
                          </span>
                        ) : (
                          // Written before this column existed, or by a service that has not been
                          // upgraded yet. "Unknown", not "no node".
                          <span style={{ color: "#cbd5e1" }} title={t("system.nodeUnknown", { defaultValue: "Not recorded — written before node identity was captured, or by a service that predates it" })}>—</span>
                        )}
                      </td>
                      <td>
                        {e.category && (
                          <div style={{ fontSize: 10, color: "#9ca3af", marginBottom: 2, fontFamily: "inherit" }}>
                            {e.category}
                          </div>
                        )}
                        <div style={{
                          fontFamily: "inherit",
                          fontSize: 12.5,
                          whiteSpace: isExp ? "pre-wrap" : "nowrap",
                          overflow: "hidden",
                          textOverflow: isExp ? "unset" : "ellipsis",
                          maxWidth: "100%",
                          color: "#111827",
                        }}>
                          {e.message}
                        </div>
                        {e.exception && isExp && (
                          <pre style={{
                            marginTop: 8,
                            padding: "8px 12px",
                            background: "#fff0f0",
                            border: "1px solid #fecaca",
                            borderRadius: 6,
                            fontSize: 11,
                            color: "#7f1d1d",
                            overflowX: "auto",
                            whiteSpace: "pre-wrap",
                          }}>
                            {e.exception}
                          </pre>
                        )}
                        {(e.exception || e.message?.length > 80) && (
                          <button
                            type="button"
                            onClick={() => setExpanded((s) => { const n = new Set(s); if (n.has(e.id)) n.delete(e.id); else n.add(e.id); return n; })}
                            style={{ border: "none", background: "none", padding: "2px 0", cursor: "pointer", color: "#9ca3af", fontSize: 11, display: "flex", alignItems: "center", gap: 3, marginTop: 2 }}
                          >
                            {isExp ? <ChevronUpIcon /> : <ChevronDownIcon />}
                          </button>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          title={t("system.copyEntry")}
                          onClick={() => navigator.clipboard.writeText(JSON.stringify(e, null, 2))}
                          style={{ border: "none", background: "transparent", padding: 6, borderRadius: 6, cursor: "pointer", color: "#9ca3af" }}
                        >
                          <CopyIcon />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Load older ── */}
        {!loading && filtered.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 12 }}>
            {nextCursor ? (
              <button
                className="adm-btn adm-btn--outline"
                disabled={loadingMore}
                onClick={loadOlder}
              >
                {loadingMore
                  ? t("system.loading", { defaultValue: "Loading logs…" })
                  : t("system.loadOlder", { defaultValue: "Load older" })}
              </button>
            ) : (
              <span style={{ fontSize: 12, color: "#9ca3af" }}>
                {t("system.endOfResults", { defaultValue: "End of results" })}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDateTime(ts) {
  try {
    const d = new Date(ts);
    return [
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, "0"),
      String(d.getDate()).padStart(2, "0"),
    ].join("-") + " " + [
      String(d.getHours()).padStart(2, "0"),
      String(d.getMinutes()).padStart(2, "0"),
      String(d.getSeconds()).padStart(2, "0"),
    ].join(":");
  } catch { return String(ts); }
}

function csvEscape(v) {
  const s = String(v ?? "");
  return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

// A WORKER_ID/GATHERER_ID is a GUID. The full value stays in the title attribute and the CSV;
// the cell shows enough to tell two instances on one host apart.
function shortInstance(id) {
  return id.length > 10 ? id.slice(0, 8) : id;
}

function exportCsv(entries) {
  try {
    const rows = [
      ["timestamp", "level", "source", "node", "instance", "category", "message", "exception"],
      ...entries.map((e) => [e.timestamp, e.level, e.source, e.nodeId ?? "", e.instanceId ?? "", e.category, e.message, e.exception ?? ""]),
    ];
    const csv  = rows.map((r) => r.map(csvEscape).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.download = `system-logs-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) { console.error("CSV export failed", err); }
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div style={{ padding: 48, display: "flex", flexDirection: "column", alignItems: "center", gap: 10, color: "#9ca3af" }}>
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <polyline points="10 9 9 9 8 9"></polyline>
      </svg>
      <div style={{ fontWeight: 600, color: "#374151" }}>{t("system.empty.title")}</div>
      <div style={{ fontSize: 13 }}>{t("system.empty.subtitle")}</div>
    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function Svg({ size = 14, children }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size}
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden focusable="false" style={{ display: "inline-block", verticalAlign: "middle" }}>
      {children}
    </svg>
  );
}

function RefreshIcon()    { return <Svg><polyline points="23 4 23 10 17 10"></polyline><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></Svg>; }
function DownloadIcon()   { return <Svg><path d="M21 15v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></Svg>; }
function TrashIcon()      { return <Svg><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"></path><path d="M10 11v6"></path><path d="M14 11v6"></path><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path></Svg>; }
function SearchIcon()     { return <Svg size={15}><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></Svg>; }
function CopyIcon()       { return <Svg size={14}><rect x="9" y="9" width="11" height="11" rx="2"></rect><rect x="4" y="4" width="11" height="11" rx="2"></rect></Svg>; }
function ChevronDownIcon(){ return <Svg size={12}><polyline points="6 9 12 15 18 9"></polyline></Svg>; }
function ChevronUpIcon()  { return <Svg size={12}><polyline points="18 15 12 9 6 15"></polyline></Svg>; }

function SpinnerIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round">
      <line x1="12" y1="2" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="22" />
      <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" /><line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
      <line x1="2" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="22" y2="12" />
      <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" /><line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
    </svg>
  );
}
